/**
 * WhatsappConfirmButton — botón para confirmar asistencia del paciente por
 * WhatsApp, usado en AppointmentModal (terapeuta) y ClinicAppointmentModal
 * (director).
 *
 * Solo se renderiza si:
 *   1. La consulta es exactamente mañana (zona horaria America/Santiago).
 *   2. El teléfono del paciente normaliza a un número válido de WhatsApp.
 * Si cualquiera de las dos condiciones falla, el botón no se muestra — no
 * hay mensaje de fecha genérica ni botón deshabilitado, se decidió así.
 */
import React from 'react';
import { toSantiagoISO } from '../../lib/utils/dateUtils';
import {
  normalizePhoneForWhatsapp,
  isConsultationTomorrow,
  buildWhatsappConfirmationMessage,
} from '../../lib/utils/whatsappConfirmation';

interface WhatsappConfirmButtonProps {
  patientFirstName: string;
  patientPhone: string | null | undefined;
  /** Fecha de la consulta, YYYY-MM-DD (Santiago). */
  date: string;
  /** Hora de inicio de la consulta, HH:MM (Santiago). */
  time: string;
  /** Hora de término de la consulta, HH:MM (Santiago). */
  endTime: string;
  /** Se llama tras abrir WhatsApp — el llamador la usa para cerrar el detalle de la consulta. */
  onSent?: () => void;
}

const WhatsappConfirmButton: React.FC<WhatsappConfirmButtonProps> = ({
  patientFirstName,
  patientPhone,
  date,
  time,
  endTime,
  onSent,
}) => {
  const numero = normalizePhoneForWhatsapp(patientPhone);
  if (!numero) return null;

  const fechaHoraConsulta = new Date(toSantiagoISO(date, time));
  if (!isConsultationTomorrow(fechaHoraConsulta)) return null;

  const fechaHoraFinConsulta = new Date(toSantiagoISO(date, endTime));

  const handleClick = () => {
    const mensaje = buildWhatsappConfirmationMessage(patientFirstName, fechaHoraConsulta, fechaHoraFinConsulta);
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(mensaje)}`, '_blank', 'noopener,noreferrer');
    onSent?.();
  };

  return (
    <button type="button" className="whatsapp-confirm-btn" onClick={handleClick}>
      <i className="fab fa-whatsapp" />
      Confirmar por WhatsApp
    </button>
  );
};

export default WhatsappConfirmButton;
